import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  NgbDatepickerModule,
  NgbTimepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { BankAccountModalComponent } from '../bank-account-modal/bank-account-modal.component';
import { ImageType } from '../../../../../core/data/models/ImageType';
import { SwalService } from '../../../../../common/services/swal.service';
import { SharedModule } from '../../../../../common/shared.module';
import { FilePondModule } from 'ngx-filepond';
import pictureTypes from '../../../../../common/enums/pictureTypes';
import { FormInputDirective } from '../../../../../common/directives/form-input.directive';
import { ClaimRequestService } from '../../../services/claim.request.service';
import { HealthRefundService } from '../../../services/health.refund.service';
import { BankService } from '../../../services/bank.service';
import acceptedImages from '../../../../../common/constants/acceptedImages';
import HealthInsured from '../../../../../core/data/models/health.insured.model';
import { CreateClaimRequestDto } from '../../../../../core/data/dtos/create.claim.request.dto';
import { lastValueFrom } from 'rxjs';
import {
  CURRENCY_TYPES,
  DOCUMENTS_RULES,
  ERROR_MESSAGES,
  PAYMENT_METHOD_CODE,
  SERVICE_TYPES,
} from '../../../utils/claimRequestConstants';
import { HealthPictureType } from '../../../../../core/data/models/health.picture.type.model';
import BaseResponse from '../../../../../core/data/models/baseModels/baseResponse.model';
import Bank from '../../../../../core/data/models/bank.model';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from '../../../../../common/utils/custom-date-parser-formatter';

@Component({
    selector: 'app-claim-request-form-step',
    imports: [
    NgSelectModule,
    FormsModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    ReactiveFormsModule,
    SharedModule,
    FilePondModule,
    NgxCurrencyDirective
],
    providers: [
        ClaimRequestService,
        BankService,
        HealthRefundService,
        SwalService,
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    ],
    templateUrl: './claim-request-form-step.component.html',
    styleUrl: './claim-request-form-step.component.css'
})
export class ClaimRequestFormStepComponent implements OnInit {
  @ViewChildren(FormInputDirective)
  validatedInputs!: QueryList<FormInputDirective>;
  @Input() idNumber?: string;
  @Output() onFormCompleted = new EventEmitter<number>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() onGoBack = new EventEmitter<void>();
  @Input() insuredSelected?: HealthInsured | null = null;
  @Input() insuredHolder?: HealthInsured | null = null;

  currencyOptions = CURRENCY_TYPES;

  serviceOptions = SERVICE_TYPES;

  bankAccountList: Bank[] = [];

  bankAccountOptions: Bank[] = [];

  holderHasBankAccount = false;

  picOptions = {
    multiple: false,
    labelIdle: '📎 Adjuntar',
    acceptedFileTypes: acceptedImages,
    allowReorder: true,
  };

  documentTypes: HealthPictureType[] = [];

  commonImages: (ImageType & { id: number })[] = [];

  protected readonly pictureTypes = pictureTypes;

  documentRules = DOCUMENTS_RULES;

  today: NgbDateStruct = this.toNgbDate(new Date());

  constructor(
    private fb: FormBuilder,
    private claimRequestService: ClaimRequestService,
    private bankService: BankService,
    private healthRefundService: HealthRefundService,
    private swalService: SwalService,
    private modalService: NgbModal,
  ) {}

  toNgbDate(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  noFutureDateValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const date: NgbDateStruct = control.value;
    if (!date) return null;

    const inputDate = new Date(date.year, date.month - 1, date.day);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return inputDate > now ? { futureDate: true } : null;
  };

  toISODateString(date: NgbDateStruct): string {
    if (!date) return '';
    const year = date.year;
    const month = date.month.toString().padStart(2, '0');
    const day = date.day.toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  claimRequestForm = this.fb.group({
    currency: [81, Validators.required],
    bankAccount: [{ value: 0, disabled: true }, Validators.required],
    serviceDate: [
      this.today,
      [Validators.required, this.noFutureDateValidator],
    ],
    serviceReason: ['', Validators.required],
    service: [null, Validators.required],
    paymentMethod: [3],
    claimedAmount: [
      '',
      [
        Validators.required,
        Validators.min(1),
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
      ],
    ],
    provider: [
      '',
      [
        Validators.required,
        Validators.pattern(
          /^[A-Za-zÁÉÍÓÚáéíóúÑñ]+(?: [A-Za-zÁÉÍÓÚáéíóúÑñ]+)*$/,
        ),
      ],
    ],
  });

  get formImages() {
    return this.commonImages;
  }

  ngOnInit(): void {
    this.claimRequestForm
      .get('service')
      ?.valueChanges.subscribe((serviceCode) => {
        this.updateDocumentTypes(Number(serviceCode));
      });

    this.getBanksInfo();
  }

  moveCursorToEnd(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (input.setSelectionRange && typeof value === 'string') {
      setTimeout(() => {
        input.setSelectionRange(value.length, value.length);
      });
    }
  }

  async getBanksInfo() {
    try {
      this.swalService.showLoading();

      const banksData = await lastValueFrom(this.bankService.getBanks());

      if (banksData?.data?.length > 0) {
        this.bankAccountList = banksData.data;

        const holderBankAccount = await lastValueFrom(
          this.healthRefundService.getMyBankAccount(
            this.insuredHolder?.idNumber!,
          ),
        );

        if (holderBankAccount?.succeeded && holderBankAccount?.data) {
          this.holderHasBankAccount = true;

          this.bankAccountOptions = [
            {
              id: holderBankAccount?.data?.bankNumber,
              description:
                banksData?.data?.find(
                  (bank) => holderBankAccount?.data?.bankNumber === bank.id,
                )?.description +
                  ' - ' +
                  holderBankAccount?.data?.accountNumber || 'Sin descripción',
            },
          ];

          this.claimRequestForm.controls.bankAccount.setValue(
            holderBankAccount?.data?.bankNumber,
          );
        } else {
          this.swalService.dismiss();
          this.swalService.error(
            'Aún no tienes registrada una cuenta de banco. Puedes agregar una nueva haciendo click en el botón "Agregar cuenta" en este mismo formulario.',
            'info',
          );
          return;
        }

        this.swalService.dismiss();
      } else {
        this.swalService.dismiss();
      }
    } catch (e) {
      this.swalService.dismiss();
    }
  }

  preventInvalidKeys(event: KeyboardEvent) {
    const invalidKeys = ['e', 'E', '+', '-'];
    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  preventInvalidProviderCharacters(event: KeyboardEvent) {
    const allowedRegex = /^[a-zA-ZÁÉÍÓÚáéíóúÑñ ]$/;
    const key = event.key;

    if (
      key === 'Backspace' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab'
    ) {
      return;
    }

    // Evitar múltiples espacios seguidos
    const input = event.target as HTMLInputElement;
    if (key === ' ' && input.value.endsWith(' ')) {
      event.preventDefault();
      return;
    }

    // Bloquear si no es una letra ni espacio
    if (!allowedRegex.test(key)) {
      event.preventDefault();
    }
  }

  updateDocumentTypes(serviceCode: number) {
    const rules = this.documentRules[serviceCode] || [];

    this.documentTypes = rules.map((rule) => ({
      ...rule,
      files: [],
    }));
  }

  handleDocumentAdd(event: any, doc: any) {
    const file = event.file.file;
    doc.files.push(file);

    this.commonImages.push({
      id: event.file.id,
      file,
      type: doc.type,
      description: doc.description,
    });
  }

  handleDocumentRemove(event: any, doc: any) {
    const index = doc.files.findIndex(
      (f: any) => f.name === event.file.filename,
    );
    if (index > -1) {
      doc.files.splice(index, 1);
    }

    const imageIndex = this.commonImages.findIndex(
      (img) => img.id === event.file.id,
    );
    if (imageIndex > -1) {
      this.commonImages.splice(imageIndex, 1);
    }
  }

  getFilePondOptions(label: string) {
    return {
      ...this.picOptions,
      labelIdle: label,
    };
  }

  onBankAccountAdded(bank: Bank) {
    this.claimRequestForm.controls.bankAccount.setValue(bank.id);
    this.bankAccountOptions = [bank];
    this.holderHasBankAccount = true;
  }

  openAddAccountModal() {
    const modalRef = this.modalService.open(BankAccountModalComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.banks = this.bankAccountList;
    modalRef.componentInstance.insuredHolder = this.insuredHolder;
    modalRef.componentInstance.onBankAccountAdded =
      this.onBankAccountAdded.bind(this);

    modalRef.result
      .then((result) => {
        if (result) {
          const newAccountLabel = `${this.getBankLabel(result.bank)} - ${result.accountNumber}`;
          this.bankAccountList.push({
            id: Date.now(),
            description: newAccountLabel,
          });
        }
      })
      .catch(() => {});
  }

  getBankLabel(bankId: number): string {
    return (
      this.bankAccountOptions
        .find((b) => b.id === bankId)
        ?.description?.split('-')[0]
        .trim() || 'Banco'
    );
  }

  uploadImages = async (claimRequestId: number) => {
    await Promise.all(
      this.formImages.map(async (i) => {
        const imgResult = await lastValueFrom(
          this.claimRequestService.upload<BaseResponse<number>>(
            i.file,
            `ClaimRequests/${claimRequestId}/File?description=${i.description}`,
          ),
        );
        return {
          imageId: imgResult.data,
          type: i.type,
        };
      }),
    );
  };

  validateRequiredDocuments(): boolean {
    for (const doc of this.documentTypes) {
      if (doc.required && (!doc.files || doc.files.length === 0)) {
        return false;
      }
    }
    return true;
  }

  onSubmit = async () => {
    this.validatedInputs.forEach((i) => {
      i.validateControl();
    });

    if (this.claimRequestForm.invalid) {
      this.claimRequestForm.markAllAsTouched();

      const firstInvalidElement = document.querySelector('.is-invalid');
      if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return;
    }

    if (!this.validateRequiredDocuments()) {
      this.swalService.error(
        'Favor adjuntar todos los documentos marcados como requeridos *.',
      );
      return;
    }

    this.swalService.showLoading('Solicitando reembolso');

    try {
      const values = this.claimRequestForm.value;

      const amountStr = this.claimRequestForm.value.claimedAmount;
      const cleanedAmount = parseFloat(
        amountStr!.toString().replace(/[^0-9.-]+/g, ''),
      );
      const dateStruct = values.serviceDate;
      const formattedDate = this.toISODateString(dateStruct!);

      const request: CreateClaimRequestDto = {
        policyNumber: this.insuredSelected?.policyNumber!,
        insured: this.insuredSelected?.insured!,
        dependent: this.insuredSelected?.dependent!,
        claimDate: formattedDate,
        diagnosticCode_Desc: values.serviceReason!,
        serviceCode: values.service!,
        providerCode_Desc: values.provider!,
        paymentMethod: PAYMENT_METHOD_CODE.TRANSFERENCIA,
        currency: values.currency!,
        amount: cleanedAmount,
      };

      const result = await lastValueFrom(
        this.claimRequestService.createClaimRequest(request),
      );

      if (result?.succeeded && result?.data) {
        const claimRequestId = result?.data;

        try {
          await this.uploadImages(Number(claimRequestId));
        } catch (e) {}

        this.swalService.dismiss();

        this.onFormCompleted.emit(Number(claimRequestId));

        this.swalService
          .success(
            `Solicitud de reembolso ${claimRequestId?.toString()?.padStart(8, '0')} creada satisfactoriamente.`,
          )
          .then((result) => {
            if (result.isConfirmed) {
              this.nextStep.emit();
            }
          });
      } else {
        this.swalService.error(
          ERROR_MESSAGES.UNEXPECTED_ERROR_CREATING_REQUEST,
        );
      }
    } catch (e) {
      this.swalService.dismiss();
      this.swalService.error(ERROR_MESSAGES.UNEXPECTED_ERROR_CREATING_REQUEST);
      return;
    }
  };

  goBack() {
    this.onGoBack.emit();
  }
}
